/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { isIOS, isMac, isTouchDevice } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isSafari } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { SWITCH_PREVIEW_STATE } from "../constants/enums";

export const getIntl = stores => stores.appStore.intl;

export const isSearchFeatureEnabled = stores => stores.appStore.isSearchEnabled;

export const isReadOnlyModeEnabled = stores => stores.appStore.isReadOnlyModeEnabled;

export const isZoomFeatureEnabled = stores => stores.appStore.isZoomEnabled;

export const isRHPResizing = stores => stores.appStore.isRHPResizing;

export const isExplicitlyDisabledFeature = (stores, feature) => {
  if (stores.appStore.explicitlyDisabledFeatures.includes(feature)) return true;
  return false;
};

export const isDownloadFeatureEnabled = stores => {
  if (isIOS() || (isMac() && isTouchDevice())) return isSafari() && stores.appStore.isDownloadEnabled;
  return stores.appStore.isDownloadEnabled;
};

export const isPrintFeatureEnabled = stores => stores.appStore.isPrintEnabled;

export const isTextSelectionEnabled = stores => !stores.appStore.isPanActive && stores.appStore.isTextSelectionEnabled;

export const isPanActive = stores => stores.appStore.isPanActive;

export const getWindowHeight = stores => stores.appStore.windowHeight;

export const getIsParentWebsiteScrolling = stores => stores.appStore.isParentWebsiteScrolling;

export const getSelectedTool = stores => stores.appStore.selectedTool;

export const getShouldUseModernViewer = stores => stores.appStore && stores.appStore.shouldUseModernViewer;

export const getShouldUseMobileModernViewer = stores => stores.appStore && stores.appStore.shouldUseMobileModernViewer;

export const getCommentsAdditionalConfig = stores => stores.appStore && stores.appStore.commentsAdditionalConfig;

export const getSelectedDropinForRHP = stores => stores.appStore.selectedDropinForRHP;

export const getRhpSkeletonDropinId = stores => stores.appStore.rhpSkeletonDropinId;

export const getCurrRHPFeature = stores => stores.appStore.currRHPFeature;

export const getEnableLinearization = stores => stores.appStore.enableLinearization;

export const getEnableCNPDF = stores => stores.appStore.enableCNPDF;

export const getUseIframelessPrint = stores => stores.appStore.useIframelessPrint;

export const getUseWindowPrint = stores => stores.appStore.useWindowPrint;

export const getIsFallbackPrintEnabled = stores => stores.appStore.useFallbackPrint;

export const getFontAccessType = stores => stores.appStore.fontAccessType;

export const getEnableEditForFreeUsers = stores => stores.appStore.enableEditForFreeUsers;

export const getEditEnterInfo = stores => stores.appStore.editEnterInfo;

export const getUseOldBinary = stores => stores.appStore.useOldBinary;

export const getJSEngine = stores => stores.appStore.jsEngine;

export const getJsSAB = stores => stores.appStore.jsSAB;

export const getIsJSScriptExecuting = stores => stores.appStore.isJSScriptExecuting;

export const getUseAFFontPicker = stores => stores.appStore.useAFFontPicker;

export const getDisableEditFeatures = stores => stores.appStore.disableEditFeatures;

export const getShouldEnableEditGenAI = stores => stores.appStore.shouldEnableEditGenAI;

export const getOverlaysManager = stores => stores.appStore.overlaysManager;

export const getUseViewSDKForAcroformSubmit = stores => stores.appStore.useViewSDKForAcroformSubmit;

export const getKillSwitchJSOverridePromise = stores => stores.appStore.killSwitchJSOverridePromise;

export const getSupportPDFJavaScriptPromise = stores => {
  if (!getEnablePDFJavaScriptFeatureFlag(stores)) return Promise.resolve(false);

  return stores.appStore.supportPDFJavaScriptPromise;
};

export const getSupportPDFJavaScript = stores =>
  getEnablePDFJavaScriptFeatureFlag(stores) && stores.appStore.supportPDFJavaScript;

export const getShowGetAppDialog = stores => stores.appStore.showGetAppDialog;

export const getShouldShowInteractiveTooltips = stores => stores.appStore.shouldShowInteractiveTooltips;

export const getIsDisabledAcroformValidationFormatting = stores => !stores.appStore.enableAcroformValidationFormatting;

export const getEnablePDFJavaScriptFeatureFlag = stores => stores.appStore.enablePDFJavaScriptFeatureFlag;

export const getLinearizationBlockSize = stores => stores.appStore.linearizationBlockSize;

export const getSuppressPasswordDialog = stores => stores.appStore.suppressPasswordDialog;

export const getFallbackPrintTimeout = stores => stores.appStore.fallbackPrintTimeout;

export const getEnableOverprintSupport = stores => stores.appStore.enableOverprintSupport;

export const getFileLocation = stores => stores.appStore.fileLocation;

export const getFillSignConfig = stores => stores.appStore.fillSignConfig;

export const isCopyWithFormattingEnabled = stores => stores.appStore.isCopyWithFormattingEnabled;

export const getShowPremiumIconForNonDCWebClients = stores => stores.appStore.showPremiumIconForNonDCWebClients;

export const getShowPremiumIndicatorForEditMegaVerb = stores => stores.appStore.usePremiumIndicator;

export const getShouldEnableOutline = stores => stores.appStore.shouldEnableOutline;

export const getShouldEnableQnA = stores => stores.appStore.shouldEnableQnA;

export const getHideStickyToolOnTap = stores => stores.appStore.hideStickyToolOnTap;

export const getShouldEnableRHPResizing = stores => stores.appStore.enableRHPResizing;

export const isDarkPageThemeEnabled = stores =>
  stores.pageThemeSwitcherStore.isDarkPageThemeEnabled && !stores.pageThemeSwitcherStore.isDarkPageThemeSwitchDisabled;

export const getPageThemeColorPallette = stores => stores.pageThemeSwitcherStore.colorPallete;

export const getShouldShowAnimation = stores => stores.appStore.shouldShowAnimation;

export const getGenAIViewerController = stores => stores.appStore.genAIViewerController;

export const getShowAssistantInRHP = stores => stores.appStore.showAssistantInRHP;

export const getSwitchPreviewState = stores => stores.appStore.switchPreviewState;

export const getIsPreviewMounted = stores => stores.appStore.switchPreviewState !== SWITCH_PREVIEW_STATE.SWITCHING;

export const getPopupConfig = stores => stores.appStore.popupConfig;

export const getShouldShowPopup = stores => stores.appStore.shouldShowPopup;
export const getShouldEnableInlineOrganize = stores => stores.appStore.shouldEnableInlineOrganize;

export const getShouldUseNewThumbnailsGrid = stores => stores.appStore.shouldUseNewThumbnailsGrid;

export const getIsMultiDocModeEnabled = stores => stores.appStore.isMultiDocModeEnabled;

export const getJSEngineInitContextsParams = stores => stores.appStore.jsEngineInitContextsParams;

export const getImageClickSend = stores => stores.appStore.imageClickSend;

export const getShouldDisableProtectedPdfCancel = stores => stores.appStore.shouldDisableProtectedPDFCancel;
